import { useCallback, useMemo } from 'react';

import { cn, StatusChip } from '@src/components/ui';
import { ROLE_CHIP, ROLE_DESCRIPTIONS, ROLE_RANK } from '@src/constants';
import { formatDate as formatDateBase } from '@src/corelib/utils/formatUtils';
import { useOptionGroup, useOptionLabels } from '@src/query';


/** Returns a stable `(role) => label` resolver backed by the metadata-options
 * hook (#364). MUST be called at component top level; the returned function is
 * safe to thread through props and call in event handlers / titles. */
function useRoleLabel() {
  const labels = useOptionLabels('membership_role');
  return useCallback((role) => labels[role] || role || '', [labels]);
}


/** Invitable roles as `[{ value, label, description }]`, sourced from the backend
 * (`invitable_role` group — owner excluded server-side) with the .pen descriptive
 * copy joined on. Single source for the invite modal + Team role legend, so a
 * backend change to the invitable set can never leave the UI offering wrong choices.
 * MUST be called at component top level. */
function useInvitableRoles() {
  const roles = useOptionGroup('invitable_role');
  return useMemo(
    () => roles.map(({ value, label }) => ({ value, label, description: ROLE_DESCRIPTIONS[value] || '' })),
    [roles],
  );
}


/** Assignable role values (member-row role select), server-driven from the same
 * `invitable_role` group. MUST be called at component top level. */
function useAssignableRoles() {
  const roles = useOptionGroup('invitable_role');
  return useMemo(() => roles.map(({ value }) => value), [roles]);
}


function isDowngrade(fromRole, toRole) {
  return (ROLE_RANK[toRole] ?? 0) < (ROLE_RANK[fromRole] ?? 0);
}


function initials(user) {
  const letters = [user?.first_name, user?.last_name]
    .map((part) => (part || '').charAt(0))
    .join('');
  return (letters || (user?.email || '?').charAt(0)).toUpperCase();
}


function fullName(user) {
  return `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
}


/** "Jun 3, 2026" — short, locale-stable display for joined/sent dates. */
function formatDate(value) {
  return formatDateBase(value, { withYear: true });
}


/** Routes through the StatusChip primitive (#367) so role chips share one chip
 * implementation. `bordered={false}` + the ROLE_CHIP class override (merged last
 * via cn, so it wins) keep the bespoke /15 soft-tint and tight metrics
 * pixel-identical — no visible appearance change. */
function RoleChip({ role, className }) {
  const roleLabel = useRoleLabel();
  return (
    <StatusChip
      className={cn(
        'px-2.5 py-[3px] text-xs',
        ROLE_CHIP[role] || ROLE_CHIP.viewer,
        className,
      )}
      bordered={false}
    >
      {roleLabel(role)}
    </StatusChip>
  );
}

RoleChip.displayName = 'RoleChip';


/** Gradient initials avatar (members) or neutral fallback (pending invites). */
function Avatar({ user, size = 34, muted = false }) {
  const style = { width: size, height: size, fontSize: Math.round(size * 0.36) };
  if (muted) {
    return (
      <span
        style={style}
        className="grid shrink-0 place-items-center rounded-full border border-hairline-strong bg-surface-2"
      />
    );
  }
  return (
    <span
      style={style}
      className="bg-brand-gradient grid shrink-0 place-items-center rounded-full font-bold text-white"
    >
      {initials(user)}
    </span>
  );
}

Avatar.displayName = 'Avatar';

export {
  Avatar,
  formatDate,
  fullName,
  initials,
  isDowngrade,
  RoleChip,
  useAssignableRoles,
  useInvitableRoles,
  useRoleLabel,
};
